import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Card, CardContent, CardHeader, FormLabel, Select, ListSubheader, MenuItem, Slider, Stack, Switch } from '@mui/material';

import patterns from './patterns'; // Import your patterns here

const accentPatterns = {
  "2/4": ["strong", "weak"],
  "3/4": ["strong", "weak", "weak"],
  "4/4": ["strong", "weak", "mid", "weak"],
  "6/8": ["strong", "weak", "weak", "mid", "weak", "weak"],
};

export default function MetronomeWithCoDriver() {
  const [patternBpm, setPatternBpm] = useState(88);
  const [patternSignature, setPatternSignature] = useState('4/4');
  const [bpm, setBpm] = useState(88);
  const [signature, setSignature] = useState("4/4");
  const [isPlaying, setIsPlaying] = useState(false);
  const [useMetronome, setUseMetronome] = useState(true);
  const [useCoDriver, setUseCoDriver] = useState(true);
  const [useMusic, setUseMusic] = useState(false);
  const [voices, setVoices] = useState([]);
  const [introMeasures, setIntroMeasures] = useState(4);

  const beatIndex = useRef(0);
  const currentStep = useRef(0);
  const stepBeatCounter = useRef(0);
  const intervalId = useRef(null);
  const audioCtx = useRef(null);
  const cueGroups = useRef([]);
  const accentBuffers = useRef({});

  // Co-driver configuration
  //const introMeasures = useRef(8); // How many measures to play before starting the co-driver
  const cueLeadBeats = useRef(4); // announce cues 3 beat before the step begins
  const [codriverVoice, setCodriverVoice] = useState(0);



  const loadSample = async (url) => {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    const audioBuffer = await audioCtx.current.decodeAudioData(arrayBuffer);
    return audioBuffer;
  }

  const playClick = useCallback((accent) => {
    if (!useMetronome || !audioCtx.current) return;
    const buffer = accentBuffers.current[accent] || accentBuffers.current.weak;
    if (!buffer) return;

    const source = audioCtx.current.createBufferSource();
    source.buffer = buffer;
    source.connect(audioCtx.current.destination);
    source.start();
  }, [useMetronome]);

  const speakCue = useCallback((text) => {
    if (!useCoDriver || !text) return;
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = 1.1;
    utter.pitch = 0.95;
    utter.volume = 0.3;
    utter.voice = codriverVoice;
    speechSynthesis.speak(utter);
  }, [useCoDriver, codriverVoice]);

  const startMetronome = useCallback(() => {
    if (!audioCtx.current) audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
    let beatsElapsed = 0;
    const beatInterval = (60 / bpm) * 1000;
    const pattern = accentPatterns[signature] || accentPatterns["4/4"];

    beatIndex.current = 0;
    currentStep.current = 0;
    stepBeatCounter.current = 0;
    let groupLeadBeatsRemaining = cueLeadBeats.current;

    const getCurrentGroup = () => cueGroups.current[currentStep.current % cueGroups.current.length] || { cue: "", totalBeats: 0 };

    let groupTotalBeats = getCurrentGroup().totalBeats || pattern.length * 2; // Default to pattern length if not specified

    speakCue('Begin');

    intervalId.current = setInterval(() => {
      const accent = pattern[beatIndex.current % pattern.length];
      playClick(accent);
      beatsElapsed++;

      // During intro measures, just play metronome—no cues
      if (beatsElapsed <= introMeasures * pattern.length) {
        beatIndex.current++;
        return;
      }

      // Proceed as normal after intro
      if (groupLeadBeatsRemaining === cueLeadBeats.current) {
        const group = getCurrentGroup();
        speakCue(group.cue);
      }

      groupLeadBeatsRemaining--;
      stepBeatCounter.current++;

      if (stepBeatCounter.current >= groupTotalBeats) {
        currentStep.current++;
        stepBeatCounter.current = 0;
        groupLeadBeatsRemaining = cueLeadBeats.current;
        groupTotalBeats = getCurrentGroup().totalBeats;
      }

      beatIndex.current++;
    }, beatInterval);
  }, [bpm, signature, playClick, speakCue, introMeasures]);

  const stopMetronome = useCallback(() => {
    clearInterval(intervalId.current);
    intervalId.current = null;
    beatIndex.current = 0;
    currentStep.current = 0;
    stepBeatCounter.current = 0;
    speechSynthesis.cancel();
  }, []);

  const setPattern = (patternName) => {
    setIsPlaying(false);
    cueGroups.current = [];
    //setIntroMeasures(0);
    cueLeadBeats.current = 0;

    if (patterns[patternName]) {
        setPatternBpm(patterns[patternName].bpm || 88);
        setPatternSignature(patterns[patternName].signature || "4/4");
        setBpm(patterns[patternName].bpm || 88);
        setSignature(patterns[patternName].signature || "4/4");
        cueGroups.current = patterns[patternName].pattern || [];
        //setIntroMeasures(patterns[patternName].introMeasures);
        cueLeadBeats.current = patterns[patternName].signature.substring(0,1) * 2
    }
  };

  useEffect(() => {
    if (isPlaying) startMetronome();
    else stopMetronome();

    return () => stopMetronome();
  }, [isPlaying, bpm, signature, startMetronome, stopMetronome]);

  useEffect(() => {
    if ('speechSynthesis' in window) {
        const updateVoices = () => {
            setVoices(speechSynthesis.getVoices().filter(voice => voice.lang.startsWith('en')));
            setCodriverVoice(speechSynthesis.getVoices().find(voice => voice.name === "Samantha" || voice.name === "Google US English") || speechSynthesis.getVoices()[0]);
        };

        speechSynthesis.onvoiceschanged = updateVoices;
        // Listen for the voiceschanged event
        speechSynthesis.addEventListener('voiceschanged', updateVoices);

        // Call it once initially in case voices are already loaded
        updateVoices();
    } else {
        alert("Speech synthesis not supported in this browser.");
    }

    if (!audioCtx.current) {
      audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
    }

    const loadSamples = async () => {
      accentBuffers.current.strong = await loadSample("/samples/snare-808.wav");
      accentBuffers.current.mid = await loadSample("/samples/perc-808.wav");
      accentBuffers.current.weak = await loadSample("/samples/hihat-808.wav");
    };

    loadSamples();
  }, []);

  return (
    <Card>
      <CardHeader title="Ice Dance Co-Driver" />
      <CardContent>
        <Stack spacing={2}>
          <Select defaultValue="" onChange={(e) => setPattern(e.target.value)} fullWidth>
            <MenuItem value=""><em>Select dance pattern</em></MenuItem>
            <ListSubheader>Preliminary</ListSubheader>
              <MenuItem value="DutchWaltz">Dutch Waltz</MenuItem>
              <MenuItem value="TangoCanasta">Tango Canasta</MenuItem>
              <MenuItem value="RhythmBlues">Rhythm Blues</MenuItem>
            <ListSubheader>Pre-Bronze</ListSubheader>
              <MenuItem value="SwingDance">Swing Dance</MenuItem>
              <MenuItem value="ChaCha">Cha Cha</MenuItem>
              <MenuItem value="TangoFiesta">Tango Fiesta</MenuItem>
            <ListSubheader>Bronze</ListSubheader>
              <MenuItem value="HickoryHoedown">Hickory Hoedown</MenuItem>
              <MenuItem value="WillowWaltz">Willow Waltz</MenuItem>
              <MenuItem value="TenFox">Ten-Fox</MenuItem>
            <ListSubheader>Pre-Silver</ListSubheader>
              <MenuItem value="Forteenstep">Fourteenstep</MenuItem>
              <MenuItem value="EuropeanWaltz">European Waltz</MenuItem>
              <MenuItem value="Foxtrot">Foxtrot</MenuItem>
            <ListSubheader>Silver</ListSubheader>
              <MenuItem value="AmericanWaltz">American Waltz</MenuItem>
              <MenuItem value="Tango">Tango</MenuItem>
              <MenuItem value="RockerFoxtrot">Rocker Foxtrot</MenuItem>
            <ListSubheader>Pre-Gold</ListSubheader>
              <MenuItem value="Kilian">Kilian</MenuItem>
              <MenuItem value="Blues">Blues</MenuItem>
              <MenuItem value="PasoDobleLead">Paso Doble - Lead</MenuItem>
              <MenuItem value="PasoDobleFollow">Paso Doble - Follow</MenuItem>
              <MenuItem value="StarlightWaltzLead">Starlight Waltz - Lead</MenuItem>
              <MenuItem value="StarlightWaltzFollow">Starlight Waltz - Follow</MenuItem>
            <ListSubheader>Gold</ListSubheader>
              <MenuItem value="VienneseWaltzLead">Viennese Waltz - Lead</MenuItem>
              <MenuItem value="VienneseWaltzFollow">Viennese Waltz - Follow</MenuItem>
              <MenuItem value="WestminsterWaltzLead">Westminster Waltz - Lead</MenuItem>
              <MenuItem value="WestminsterWaltzFollow">Westminster Waltz - Follow</MenuItem>
              <MenuItem value="Quickstep">Quickstep</MenuItem>
              <MenuItem value="ArgentineTangoLead">Argentine Tango - Lead</MenuItem>
              <MenuItem value="ArgentineTangoFollow">Argentine Tango - Follow</MenuItem>
            <ListSubheader>International</ListSubheader>
              <MenuItem value="AustrianWaltzLead">Austrian Waltz - Lead</MenuItem>
              <MenuItem value="AustrianWaltzFollow">Austrian Waltz - Follow</MenuItem>
              <MenuItem value="ChaChaCongelado">Cha Cha Congelado</MenuItem>
              <MenuItem value="FinnstepLead">Finnstep - Lead</MenuItem>
              <MenuItem value="FinnstepFollow">Finnstep - Follow</MenuItem>
              <MenuItem value="GoldenWaltzLead">Golden Waltz - Lead</MenuItem>
              <MenuItem value="GoldenWaltzFollow">Golden Waltz - Follow</MenuItem>
              <MenuItem value="MidnightBluesLead">Midnight Blues - Lead</MenuItem>
              <MenuItem value="MidnightBluesFollow">Midnight Blues - Follow</MenuItem>
              <MenuItem value="RavensburgerWaltzLead">Ravensburger Waltz - Lead</MenuItem>
              <MenuItem value="RavensburgerWaltzFollow">Ravensburger Waltz - Follow</MenuItem>
              <MenuItem value="Rhumba">Rhumba</MenuItem>
              <MenuItem value="SilverSambaLead">Silver Samba - Lead</MenuItem>
              <MenuItem value="SilverSambaFollow">Silver Samba - Follow</MenuItem>
              <MenuItem value="TangoRomanticaLead">Tango Romantica - Lead</MenuItem>
              <MenuItem value="TangoRomanticaFollow">Tango Romantica - Follow</MenuItem>
              <MenuItem value="TeaTimeFoxtrotLead">Tea-Time Foxtrot - Lead</MenuItem>
              <MenuItem value="TeaTimeFoxtrotFollow">Tea-Time Foxtrot - Follow</MenuItem>
              <MenuItem value="YankeePolkaLead">Yankee Polka - Lead</MenuItem>
              <MenuItem value="YankeePolkaFollow">Yankee Polka - Follow</MenuItem>
          </Select>
          <FormLabel>Tempo (BPM): {bpm}/<em>{patternBpm}</em></FormLabel>
          <Slider
            defaultValue={88}
            value={bpm}
            onChange={(e, newValue) => setBpm(newValue)}
            min={30}
            max={240}
            step={1}
            valueLabelDisplay="auto"
            marks
            valueLabelFormat={(value) => `${value} BPM`}
          />
          <FormLabel>Time Signature: <em>{patternSignature}</em></FormLabel>
          <Select value={signature} onChange={(e) => setSignature(e.target.value)}>
            {Object.keys(accentPatterns).map(sig => (
              <MenuItem key={sig} value={sig}>{sig}</MenuItem>
            ))}
          </Select>
          <FormLabel>Intro Measures: {introMeasures}</FormLabel>
          <Slider
            defaultValue={4}
            min={0}
            max={16}
            step={1}
            valueLabelDisplay="auto"
            value={introMeasures}
            onChange={(e, newValue) => {
              setIntroMeasures(newValue);
            }}
          />
          <FormLabel>Co-Driver Voice</FormLabel>
          <Select
            value={codriverVoice ? codriverVoice.name : ''}
            onChange={(e) => {
              const selectedVoice = voices.find(voice => voice.name === e.target.value);
              if (selectedVoice) {
                setCodriverVoice(selectedVoice);
              }
            }}
          >
            {voices.map(voice => (
              <MenuItem key={voice.name} value={voice.name}>
                {voice.name} ({voice.lang})
              </MenuItem>
            ))}
          </Select>
          <Box>
            <Switch
              checked={useMetronome}
              onChange={(e) => setUseMetronome(e.target.checked)}
              color="primary"
            />
            <FormLabel>Metronome</FormLabel>
            <Switch
              checked={useCoDriver}
              onChange={(e) => setUseCoDriver(e.target.checked)}
              color="primary"
            />
            <FormLabel>Co-Driver</FormLabel>
            {/*
            <Switch
              checked={useMusic}
              onChange={(e) => setUseMusic(e.target.checked)}
              color="primary"
              disabled
            />
            <FormLabel>Music</FormLabel>
            */}
          </Box>
          <Button onClick={() => setIsPlaying(!isPlaying)} variant="contained" color={isPlaying ? "secondary" : "primary"}>
            {isPlaying ? "Stop" : "Start"}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}